// components/customer/CustomerNotes.js
import React, { useState, useEffect } from 'react';
import { Card, CardContent, Button, Loading, ErrorMessage } from '../common';
import { CustomerNotesAPI } from '../../services/api/customerNotesAPI';
import { useTranslations } from '../../hooks/useTranslations';

export function CustomerNotes({ customerId }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [newNoteText, setNewNoteText] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const t = useTranslations();

  // Load notes when component mounts or customerId changes
  useEffect(() => {
    if (customerId) {
      loadNotes();
    }
  }, [customerId]);

  const loadNotes = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedNotes = await CustomerNotesAPI.getCustomerNotes(customerId);
      setNotes(fetchedNotes);
    } catch (err) {
      console.error('Error loading notes:', err);
      setError(err.message || 'Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNoteText.trim()) {
      return;
    }

    try {
      setSubmitting(true);
      const createdNote = await CustomerNotesAPI.createNote(customerId, newNoteText);
      setNotes([createdNote, ...notes]); // Add to beginning
      setNewNoteText('');
      setIsAdding(false);
    } catch (err) {
      console.error('Error adding note:', err);
      setError(err.message || 'Failed to add note');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartEdit = (note) => {
    setEditingNoteId(note.id);
    setEditingNoteText(note.note_text);
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditingNoteText('');
  };

  const handleSaveEdit = async (noteId) => {
    if (!editingNoteText.trim()) {
      return;
    }

    try {
      setSubmitting(true);
      const updatedNote = await CustomerNotesAPI.updateNote(noteId, editingNoteText);
      setNotes(notes.map(n => n.id === noteId ? updatedNote : n));
      setEditingNoteId(null);
      setEditingNoteText('');
    } catch (err) {
      console.error('Error updating note:', err);
      setError(err.message || 'Failed to update note');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm(t.confirmDeleteNote || 'Are you sure you want to delete this note?')) {
      return;
    }

    try {
      setSubmitting(true);
      await CustomerNotesAPI.deleteNote(noteId);
      setNotes(notes.filter(n => n.id !== noteId));
    } catch (err) {
      console.error('Error deleting note:', err);
      setError(err.message || 'Failed to delete note');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('he-IL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Loading text={t.loadingNotes || 'Loading notes...'} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              {t.customerNotes || 'Customer Notes'}
            </h2>
            {!isAdding && (
              <Button
                onClick={() => setIsAdding(true)}
                variant="primary"
                size="small"
              >
                + {t.addNote || 'Add Note'}
              </Button>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <ErrorMessage
              message={error}
              onDismiss={() => setError(null)}
            />
          )}

          {/* Add New Note Form */}
          {isAdding && (
            <div className="border border-gray-300 rounded-lg p-4 bg-gray-50" dir="rtl">
              <textarea
                value={newNoteText}
                onChange={(e) => setNewNoteText(e.target.value)}
                placeholder={t.enterNoteText || 'Enter note text...'}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-pink-500 focus:border-pink-500 text-right"
                disabled={submitting}
                dir="rtl"
              />
              <div className="flex gap-2 mt-3">
                <Button
                  onClick={handleAddNote}
                  variant="primary"
                  size="small"
                  disabled={submitting || !newNoteText.trim()}
                >
                  {submitting ? (t.saving || 'Saving...') : (t.save || 'Save')}
                </Button>
                <Button
                  onClick={() => {
                    setIsAdding(false);
                    setNewNoteText('');
                  }}
                  variant="secondary"
                  size="small"
                  disabled={submitting}
                >
                  {t.cancel || 'Cancel'}
                </Button>
              </div>
            </div>
          )}

          {/* Notes List */}
          {notes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>{t.noNotesYet || 'No notes yet. Add your first note above.'}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {notes.map((note) => (
                <div
                  key={note.id}
                  className="border border-gray-200 rounded-lg p-4 bg-white hover:shadow-sm transition-shadow"
                  dir="rtl"
                >
                  {editingNoteId === note.id ? (
                    // Edit Mode
                    <div>
                      <textarea
                        value={editingNoteText}
                        onChange={(e) => setEditingNoteText(e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-pink-500 focus:border-pink-500 text-right"
                        disabled={submitting}
                        dir="rtl"
                      />
                      <div className="flex gap-2 mt-3">
                        <Button
                          onClick={() => handleSaveEdit(note.id)}
                          variant="primary"
                          size="small"
                          disabled={submitting || !editingNoteText.trim()}
                        >
                          {submitting ? (t.saving || 'Saving...') : (t.save || 'Save')}
                        </Button>
                        <Button
                          onClick={handleCancelEdit}
                          variant="secondary"
                          size="small"
                          disabled={submitting}
                        >
                          {t.cancel || 'Cancel'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // View Mode
                    <div>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <p className="text-sm text-gray-900 whitespace-pre-wrap break-words text-right">
                            {note.note_text}
                          </p>
                        </div>
                        <div className="flex gap-2 ms-4">
                          <button
                            onClick={() => handleStartEdit(note)}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                            disabled={submitting}
                            title={t.edit || 'Edit'}
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDeleteNote(note.id)}
                            className="text-red-600 hover:text-red-800 text-sm"
                            disabled={submitting}
                            title={t.delete || 'Delete'}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>
                          📅 {t.created || 'Created'}: {formatDateTime(note.created_at)}
                        </span>
                        {note.updated_at !== note.created_at && (
                          <span>
                            ✏️ {t.edited || 'Edited'}: {formatDateTime(note.updated_at)}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
